import { Assets, BitmapFont, Cache } from 'pixi.js';
import type { BitmapFontData, RawCharData, Texture } from 'pixi.js';
import type { BakedFontManifest } from './bakedManifest';
import fontManifestJson from '../../assets/baked/font/standard.json';
import fontAtlasUrl from '../../assets/baked/font/standard.webp?url';

/**
 * The family name overhead labels reference (`new BitmapText({ style: {
 * fontFamily: OVERHEAD_FONT_FAMILY } })`). Matches the `fontFamily` the offline
 * `fonts.mul` bake writes into the manifest.
 */
export const OVERHEAD_FONT_FAMILY = (fontManifestJson as BakedFontManifest).fontFamily;

let installed = false;

/**
 * The Cache key `BitmapText` resolves a `fontFamily` to (`<family>-bitmap`), the
 * same key Pixi's own `.fnt` loader uses. Both the real loader and the headless
 * metrics-only path register under this so a label finds the font.
 */
export const OVERHEAD_FONT_CACHE_KEY = `${(fontManifestJson as BakedFontManifest).fontFamily}-bitmap`;

/**
 * Build the Pixi `BitmapFontData` for the baked overhead font from the offline
 * `fonts.mul` manifest: the per-glyph metrics (advance, offsets, atlas rect) plus
 * the font-wide line metrics. This is the texture-independent half of the font -
 * everything `getBitmapTextLayout` needs to measure and **wrap** text - so it can
 * be registered without the atlas image for headless measurement (see the
 * `bakedFont.testutil` installer), or paired with the real atlas to also draw.
 */
export function buildOverheadFontData(): BitmapFontData {
  const manifest = fontManifestJson as BakedFontManifest;
  const chars: Record<string, RawCharData> = {};
  for (const g of manifest.glyphs) {
    const letter = String.fromCharCode(g.code);
    chars[letter] = {
      id: g.code,
      page: 0,
      x: g.x,
      y: g.y,
      width: g.w,
      height: g.h,
      xOffset: g.xOffset,
      yOffset: g.yOffset,
      xAdvance: g.xAdvance,
      kerning: {},
      letter,
    };
  }
  return {
    baseLineOffset: manifest.baseLineOffset,
    chars,
    pages: [{ id: 0, file: manifest.atlas }],
    lineHeight: manifest.lineHeight,
    fontSize: manifest.fontSize,
    fontFamily: manifest.fontFamily,
  };
}

/**
 * Load the baked UO overhead font (the blackletter `fonts.mul` font 3 the classic
 * 2D client uses for names and speech) and register it as a Pixi `BitmapFont` so
 * `BitmapText` can render with it. The atlas + manifest come from the offline
 * `fonts.mul` decoder; the glyph outline and light fill are baked into the atlas,
 * sampled `nearest` to keep the pixel glyphs crisp.
 *
 * The font keeps Pixi's default `applyFillAsTint`, so a `BitmapText`'s fill colour
 * tints the (near-white) glyph fill - innocent blue for names, the speaker's hue for
 * speech - while the baked black outline stays black under the multiply. Call once
 * during startup, before any overhead label is created; idempotent.
 */
export async function loadOverheadFont(): Promise<void> {
  if (installed) return;
  const atlas = await Assets.load<Texture>(fontAtlasUrl);
  atlas.source.scaleMode = 'nearest';

  const font = new BitmapFont({ data: buildOverheadFontData(), textures: [atlas] }, fontAtlasUrl);
  Cache.set(OVERHEAD_FONT_CACHE_KEY, font);
  installed = true;
}

/** Native design size of the baked font, in pixels (render at this size for 1:1). */
export const OVERHEAD_FONT_SIZE = (fontManifestJson as BakedFontManifest).fontSize;
